import { useState, useMemo, useCallback } from "react";
import { Dialog } from "@mui/material";
import { Asset } from "app/types";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { ParentCloseBtn, FadeTransition } from "common/elements";
import i18n from "features/parent/i18n";
import { AssetTabType } from "./AssetsSection";

function isAIAsset(asset: Asset): boolean {
  return asset.tags?.startsWith("ai_generated:") ?? false;
}

interface AssetListModalProps {
  assets: Asset[];
  initialTabs?: Set<AssetTabType>;
  onClose: () => void;
  onSelectAsset: (asset: Asset) => void;
  showSketchbook?: boolean;
}

export const AssetListModal = ({
  assets,
  initialTabs = new Set([AssetTabType.ALBUM, AssetTabType.AI_IMAGE]),
  onClose,
  onSelectAsset,
  showSketchbook = true,
}: AssetListModalProps) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const [selectedTabs, setSelectedTabs] = useState<Set<AssetTabType>>(initialTabs);

  const toggleTab = useCallback(
    (tabType: AssetTabType) => {
      play();
      setSelectedTabs((prev) => {
        const newTabs = new Set(prev);
        if (newTabs.has(tabType)) {
          newTabs.delete(tabType);
        } else {
          newTabs.add(tabType);
        }
        return newTabs;
      });
    },
    [play],
  );

  const filteredAssets = useMemo(() => {
    return assets.filter((asset) => {
      const isSvg = /.svg/.test(asset.url);
      const isAI = isAIAsset(asset);

      // AI画像
      if (isAI && selectedTabs.has(AssetTabType.AI_IMAGE)) return true;
      // スケッチブック（SVGかつAI以外）
      if (isSvg && !isAI && selectedTabs.has(AssetTabType.SKETCHBOOK)) return true;
      // アルバム（非SVGかつAI以外）
      if (!isSvg && !isAI && selectedTabs.has(AssetTabType.ALBUM)) return true;

      return false;
    });
  }, [assets, selectedTabs]);

  const albumCount = useMemo(
    () => assets.filter((a) => !/.svg/.test(a.url) && !isAIAsset(a)).length,
    [assets],
  );
  const sketchbookCount = useMemo(
    () => assets.filter((a) => /.svg/.test(a.url) && !isAIAsset(a)).length,
    [assets],
  );
  const aiImageCount = useMemo(() => assets.filter(isAIAsset).length, [assets]);

  const handleSelectAsset = (asset: Asset) => {
    play();
    btnDelay(() => onSelectAsset(asset));
  };

  return (
    <Dialog
      fullScreen
      open={true}
      maxWidth={false}
      sx={{ zIndex: 99999 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
    >
      <div className="flex-col-center w-full h-full bg-textcolor-black/50">
        <div style={{ transform: `scale(${scale})` }}>
          <div className="flex-col-el justify-start w-[800px] h-[620px] relative border-solid border-gray2-30 border-[16px] rounded-[16px] bg-gray2-30">
            <div className="flex-col-view justify-start w-full h-full relative p-6 rounded-[8px] bg-white">
              <ParentCloseBtn close={onClose} />
              {/* タイトル */}
              <div className="flex-row-el items-center gap-2 mb-4 pb-3 border-b border-outline">
                <span className="material-symbols-outlined !text-[54px] text-red-100">palette</span>
                <h2 className="text-[33px] text-textcolor-black">
                  {i18n.t("MSG_PARENT_PAGE_PLAYER_ASSETS_LIST")}
                </h2>
              </div>

              {/* タブ切り替え（複数選択フィルタリング） */}
              <div className="flex-row-el gap-2 mb-4">
                <button
                  className={`px-4 py-2 rounded-[12px] text-[21px] border-none transition-all ${
                    selectedTabs.has(AssetTabType.ALBUM)
                      ? "bg-ink text-white"
                      : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
                  }`}
                  onClick={() => toggleTab(AssetTabType.ALBUM)}
                >
                  {i18n.t("MSG_PARENT_PAGE_PLAYER_ALBUM")}
                  <span className="ml-1 text-[18px] opacity-80">({albumCount})</span>
                </button>
                {showSketchbook && (
                  <button
                    className={`px-4 py-2 rounded-[12px] text-[21px] border-none transition-all ${
                      selectedTabs.has(AssetTabType.SKETCHBOOK)
                        ? "bg-ink text-white"
                        : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
                    }`}
                    onClick={() => toggleTab(AssetTabType.SKETCHBOOK)}
                  >
                    {i18n.t("MSG_PARENT_PAGE_PLAYER_SKETCHBOOK")}
                    <span className="ml-1 text-[18px] opacity-80">({sketchbookCount})</span>
                  </button>
                )}
                <button
                  className={`px-4 py-2 rounded-[12px] text-[21px] border-none transition-all ${
                    selectedTabs.has(AssetTabType.AI_IMAGE)
                      ? "bg-ink text-white"
                      : "bg-gray2-30 text-textcolor-black hover:bg-gray2-40"
                  }`}
                  onClick={() => toggleTab(AssetTabType.AI_IMAGE)}
                >
                  {i18n.t("MSG_PARENT_PAGE_PLAYER_AI_IMAGE")}
                  <span className="ml-1 text-[18px] opacity-80">({aiImageCount})</span>
                </button>
              </div>

              {/* 素材グリッド */}
              <div className="flex-1 overflow-y-auto visible-scrollbar">
                {filteredAssets.length > 0 ? (
                  <div className="grid grid-cols-4 gap-4 justify-items-center">
                    {filteredAssets.map((asset) => (
                      <div
                        key={asset.id}
                        className="flex-col-el w-[160px] cursor-pointer"
                        onClick={() => handleSelectAsset(asset)}
                      >
                        <div className="w-[128px] h-[128px] rounded-[8px] overflow-hidden">
                          <img
                            src={asset.url}
                            alt={asset.name}
                            className="w-full h-full object-contain bg-white"
                          />
                        </div>
                        <div className="mt-2 text-[18px] text-gray2-100 truncate">{asset.name}</div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex-col-center py-12 text-gray2-100">
                    <span className="material-symbols-outlined text-[72px] mb-2">image</span>
                    <span className="text-[21px]">
                      {i18n.t("MSG_PARENT_PAGE_PLAYER_ASSETS_EMPTY")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
